package com.arena_web.ui.theme

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import kotlinx.browser.document
import kotlinx.browser.window

enum class ThemeMode { SYSTEM, DARK, LIGHT }

/**
 * Owns the light/dark theme state. The visual switch is pure CSS: we set
 * data-theme="light" on <html> and the --arena-* tokens flip. Compose state
 * (isLight) exists only for the few places that need per-theme logic
 * (map style, charts, meta theme-color) — layout code should rely on
 * tokens, not on this flag.
 *
 * The static <head> bootstrap in build.gradle.kts applies the attribute
 * before first paint (no FOUC); init() only reconciles state and installs
 * the system-preference listener. Silk's ColorMode stays locked to DARK
 * (AppEntry) — this controller is independent of it.
 */
object ThemeController {

    var mode: ThemeMode = ThemeMode.SYSTEM
        private set

    var isLight: Boolean by mutableStateOf(false)
        private set

    private val systemPrefersLight: Boolean
        get() = window.matchMedia("(prefers-color-scheme: light)").matches

    fun init() {
        mode = Storage.get(StorageKeys.THEME)
            ?.let { stored -> ThemeMode.entries.firstOrNull { it.name == stored } }
            ?: ThemeMode.SYSTEM
        apply(animate = false)
        window.matchMedia("(prefers-color-scheme: light)").addEventListener("change", {
            if (mode == ThemeMode.SYSTEM) apply(animate = true)
        })
    }

    fun setMode(newMode: ThemeMode) {
        mode = newMode
        Storage.save(StorageKeys.THEME, newMode.name)
        apply(animate = true)
    }

    fun toggle() {
        setMode(if (isLight) ThemeMode.DARK else ThemeMode.LIGHT)
    }

    private fun apply(animate: Boolean) {
        val light = when (mode) {
            ThemeMode.LIGHT -> true
            ThemeMode.DARK -> false
            ThemeMode.SYSTEM -> systemPrefersLight
        }
        val root = document.documentElement ?: return
        if (animate) {
            root.classList.add("theme-transition")
            window.setTimeout({ root.classList.remove("theme-transition") }, 260)
        }
        if (light) root.setAttribute("data-theme", "light")
        else root.removeAttribute("data-theme")
        // Dark value mirrors the static <meta name=theme-color> in
        // build.gradle.kts — do not diverge.
        document.querySelector("meta[name=theme-color]")
            ?.setAttribute("content", if (light) "#F7FAFF" else "#0F172A")
        isLight = light
        // Non-CSS consumers (MapLibre basemap style, canvases) re-style on
        // this event — CSS tokens flip via the attribute alone.
        window.dispatchEvent(org.w3c.dom.events.Event("arena-theme-changed"))
    }
}

/** Dev gate: light theme UI is hidden until rollout completes. */
fun themeDevEnabled(): Boolean =
    try {
        window.localStorage.getItem("theme_dev") == "1"
    } catch (e: Throwable) {
        false
    }
